import { useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { FileText, Image as ImageIcon, Loader2, Trash2, Upload } from 'lucide-react';
import { useDownloadInvoicePreview, useSettings, useUpdateSettings } from '@/api/settings';
import { useDeleteLogo, useLogoBlobUrl, useLogoStatus, useUploadLogo } from '@/api/practiceLogo';
import { useStripeAccount } from '@/api/stripeAccount';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/Dialog';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { StripeConnectionCard } from './StripeConnectionCard';
import {
    DEFAULT_STATEMENT_DESCRIPTOR,
    MAX_STATEMENT_DESCRIPTOR_LEN,
    validateStatementDescriptor,
} from './paymentsSettingsValidation';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
/*
 * Configuration → Billing → Invoice Settings. Drives the private-pay invoice
 * renderer (Phase 4+): remit-to block, payment instructions, terms, due-days,
 * plus an uploaded logo. The server enforces that `remitToName` +
 * `remitToAddress` are set before any invoice can be created — those two
 * are visually flagged as required on the form.
 *
 * The logo upload is a separate multipart endpoint; the rest are JSONB
 * fields on settings.billing and share a single "Save" button with a
 * shallow-merge PATCH so unrelated keys (oaSubmitterId etc.) stay intact.
 */

interface FormState {
    remitToName: string;
    remitToAddress: string;
    paymentInstructions: string;
    invoiceTerms: string;
    invoiceFooterNote: string;
    invoiceDueDays: string;
    portalInvoicesEnabled: boolean;
}

const EMPTY: FormState = {
    remitToName: '',
    remitToAddress: '',
    paymentInstructions: '',
    invoiceTerms: 'Due on receipt',
    invoiceFooterNote: '',
    invoiceDueDays: '0',
    portalInvoicesEnabled: true,
};

export function BillingSettingsPanel() {
    const { data, isLoading, isError } = useSettings();
    const updateM = useUpdateSettings();
    const previewM = useDownloadInvoicePreview();

    const [form, setForm] = useState<FormState>(EMPTY);
    const [err, setErr] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (!data?.billing) return;
        const b = data.billing;
        setForm({
            remitToName: b.remitToName ?? '',
            remitToAddress: b.remitToAddress ?? '',
            paymentInstructions: b.paymentInstructions ?? '',
            invoiceTerms: b.invoiceTerms ?? 'Due on receipt',
            invoiceFooterNote: b.invoiceFooterNote ?? '',
            invoiceDueDays: typeof b.invoiceDueDays === 'number' ? String(b.invoiceDueDays) : '0',
            portalInvoicesEnabled: b.portalInvoicesEnabled !== false,
        });
    }, [data]);

    function set<K extends keyof FormState>(k: K, v: FormState[K]) {
        setSaved(false);
        setErr(null);
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        setSaved(false);

        const dueDaysNum = Number(form.invoiceDueDays);
        if (!Number.isInteger(dueDaysNum) || dueDaysNum < 0 || dueDaysNum > 365) {
            setErr('Due-in days must be an integer between 0 and 365.');
            return;
        }
        if (form.invoiceTerms.length > 64) {
            setErr('Terms text must be 64 characters or fewer.');
            return;
        }

        try {
            await updateM.mutateAsync({
                type: 'billing',
                updates: {
                    remitToName: form.remitToName.trim(),
                    remitToAddress: form.remitToAddress.trim(),
                    paymentInstructions: form.paymentInstructions.trim(),
                    invoiceTerms: form.invoiceTerms.trim(),
                    invoiceFooterNote: form.invoiceFooterNote.trim(),
                    invoiceDueDays: dueDaysNum,
                    portalInvoicesEnabled: form.portalInvoicesEnabled,
                },
            });
            setSaved(true);
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        }
    }

    if (isLoading) {
        return (
            <div className="p-12 flex items-center justify-center text-text-subtle">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
        );
    }
    if (isError) {
        return <div className="p-12 text-center text-error">Couldn't load settings.</div>;
    }

    const remitReady = form.remitToName.trim() && form.remitToAddress.trim();

    return (
        <div>
            <div className="mb-1">
                <h2 className="font-display text-lg font-bold text-text">Invoice Settings</h2>
            </div>
            <p className="text-xs text-text-subtle mb-3">
                Drives the private-pay invoice PDFs. Remit-to is required before any
                invoice can be created — everything else is optional and prints only
                when set.
            </p>

            <Card className="p-5 max-w-3xl">
                <LogoSection />
                <form onSubmit={handleSubmit}>
                    <FormField
                        label="Remit to name"
                        required
                        hint="Printed at the top of every invoice — usually your practice's legal or DBA name."
                    >
                        {(id) => (
                            <Input
                                id={id}
                                value={form.remitToName}
                                onChange={(e) => set('remitToName', e.target.value.slice(0, 120))}
                                placeholder="Good Steward ABA Solutions, LLC"
                            />
                        )}
                    </FormField>

                    <FormField
                        label="Remit to address"
                        required
                        hint="Multi-line billing address. Include suite / city / state / ZIP."
                    >
                        {(id) => (
                            <Textarea
                                id={id}
                                value={form.remitToAddress}
                                onChange={(e) => set('remitToAddress', e.target.value.slice(0, 500))}
                                rows={3}
                                placeholder={'123 Main St, Suite 400\nHampton, VA 23669'}
                            />
                        )}
                    </FormField>

                    <div className="grid grid-cols-[1fr,120px] gap-3">
                        <FormField label="Terms">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.invoiceTerms}
                                    onChange={(e) => set('invoiceTerms', e.target.value.slice(0, 64))}
                                    placeholder="Due on receipt"
                                />
                            )}
                        </FormField>
                        <FormField label="Due in (days)" hint="0 = on receipt">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.invoiceDueDays}
                                    onChange={(e) => set('invoiceDueDays', e.target.value.replace(/\D/g, '').slice(0, 3))}
                                    inputMode="numeric"
                                    placeholder="0"
                                />
                            )}
                        </FormField>
                    </div>

                    <FormField
                        label="Payment instructions"
                        hint="Checks payable to, portal link, mailing address for payments — whatever you want parents to see."
                    >
                        {(id) => (
                            <Textarea
                                id={id}
                                value={form.paymentInstructions}
                                onChange={(e) => set('paymentInstructions', e.target.value.slice(0, 1000))}
                                rows={3}
                                placeholder="Make checks payable to Good Steward ABA Solutions, LLC."
                            />
                        )}
                    </FormField>

                    <FormField
                        label="Footer note"
                        hint="Optional line printed under the totals — e.g. tax-id, late-fee policy, thank-you message."
                    >
                        {(id) => (
                            <Input
                                id={id}
                                value={form.invoiceFooterNote}
                                onChange={(e) => set('invoiceFooterNote', e.target.value.slice(0, 240))}
                                placeholder="Thank you for choosing Good Steward."
                            />
                        )}
                    </FormField>

                    <label className="flex items-start gap-2 mb-4 text-sm text-text cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.portalInvoicesEnabled}
                            onChange={(e) => set('portalInvoicesEnabled', e.target.checked)}
                            className="h-4 w-4 mt-0.5"
                        />
                        <span>
                            Show invoices in the parent portal
                            <span className="block text-text-subtle">
                                When off, generated invoices are still saved here but parents won't see them in their portal — useful if you deliver invoices off-line.
                            </span>
                        </span>
                    </label>

                    {!remitReady && (
                        <p className="text-xs text-warning-text mb-3">
                            Set both Remit to name and Remit to address — invoice creation is disabled until they're filled in.
                        </p>
                    )}

                    {err && <p className="text-sm text-error mb-2">{err}</p>}
                    {saved && <p className="text-sm text-success mb-2">Saved.</p>}

                    <div className="flex justify-between items-center">
                        <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            onClick={() => previewM.mutate()}
                            disabled={previewM.isPending}
                            title="Download a sample invoice using your current settings and logo."
                        >
                            <FileText className="w-3.5 h-3.5" />
                            {previewM.isPending ? 'Rendering…' : 'Preview invoice'}
                        </Button>
                        <Button variant="primary" size="sm" type="submit" disabled={updateM.isPending}>
                            {updateM.isPending ? 'Saving…' : 'Save'}
                        </Button>
                    </div>
                </form>
            </Card>

            <Card className="p-5 max-w-3xl mt-4">
                <StripeConnectionCard />
            </Card>

            <PortalPaymentsCard />
        </div>
    );
}

/*
 * Phase 5 of docs/build-plans/pma-stripe-payments.md.
 *
 * Two settings keys, one card:
 *   - portalPaymentsEnabled — boolean opt-in. Default OFF.
 *   - paymentsStatementDescriptor — what shows on the cardholder's
 *     bank statement. Blocked clinical words because a parent's family
 *     member should not be able to read "ABA THERAPY" off the
 *     statement.
 *
 * If Stripe Connect is not finished onboarding, the card renders
 * disabled with an explanatory note. The toggle is still useful to set
 * up beforehand, but parents can't actually pay until Stripe is live —
 * the parent portal reads the COMBINED truth (this flag AND
 * charges_enabled) from /api/portal/overview's features block.
 */
function PortalPaymentsCard() {
    const { data, isLoading, isError } = useSettings();
    const stripe = useStripeAccount();
    const updateM = useUpdateSettings();

    const [enabled, setEnabled] = useState(false);
    const [descriptor, setDescriptor] = useState(DEFAULT_STATEMENT_DESCRIPTOR);
    const [descriptorError, setDescriptorError] = useState<string | null>(null);
    // Phase 8 of pma-stripe-payments — per-practice autopay defaults.
    // autopayDefault is a boolean; when true, new invoices land with
    // invoices.autopay_enabled=true so parents don't have to opt each
    // one in. autopayLeadDays is the # of days before due_date that
    // the scheduler fires (0-14; server caps at 14).
    const [autopayDefault, setAutopayDefault] = useState(false);
    const [autopayLeadDays, setAutopayLeadDays] = useState<string>('0');
    const [autopayLeadError, setAutopayLeadError] = useState<string | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (!data?.billing) return;
        setEnabled(!!data.billing.portalPaymentsEnabled);
        setDescriptor(data.billing.paymentsStatementDescriptor || DEFAULT_STATEMENT_DESCRIPTOR);
        setAutopayDefault(!!data.billing.autopayDefault);
        const leadDays = Number.isInteger(data.billing.autopayLeadDays)
            ? data.billing.autopayLeadDays
            : 0;
        setAutopayLeadDays(String(leadDays));
    }, [data]);

    const stripeReady = !!stripe.data?.charges_enabled;

    function updateDescriptor(next: string) {
        setSaved(false);
        setErr(null);
        const sliced = next.slice(0, MAX_STATEMENT_DESCRIPTOR_LEN);
        setDescriptor(sliced);
        // Empty-string-while-typing is fine; refuse to surface the
        // "cannot be empty" error until the user submits.
        setDescriptorError(sliced.trim() ? validateStatementDescriptor(sliced) : null);
    }

    function toggleEnabled(next: boolean) {
        setSaved(false);
        setErr(null);
        setEnabled(next);
    }

    function validateAutopayLeadDays(raw: string): { value: number; error: string | null } {
        if (raw.trim() === '') return { value: 0, error: 'Lead days is required.' };
        const n = Number(raw);
        if (!Number.isInteger(n)) return { value: 0, error: 'Lead days must be a whole number.' };
        if (n < 0) return { value: 0, error: 'Lead days cannot be negative.' };
        if (n > 14) return { value: 0, error: 'Lead days cannot exceed 14.' };
        return { value: n, error: null };
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        setSaved(false);

        const descriptorErr = validateStatementDescriptor(descriptor);
        if (descriptorErr) {
            setDescriptorError(descriptorErr);
            setErr(descriptorErr);
            return;
        }
        const leadCheck = validateAutopayLeadDays(autopayLeadDays);
        if (leadCheck.error) {
            setAutopayLeadError(leadCheck.error);
            setErr(leadCheck.error);
            return;
        }

        try {
            await updateM.mutateAsync({
                type: 'billing',
                updates: {
                    portalPaymentsEnabled: enabled,
                    paymentsStatementDescriptor: descriptor.trim(),
                    autopayDefault,
                    autopayLeadDays: leadCheck.value,
                },
            });
            setSaved(true);
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        }
    }

    if (isLoading) {
        return (
            <Card className="p-5 max-w-3xl mt-4">
                <div className="text-text-subtle text-sm inline-flex items-center gap-1.5">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                </div>
            </Card>
        );
    }
    if (isError) return null;

    const disabled = !stripeReady;

    return (
        <Card className="p-5 max-w-3xl mt-4">
            <div className="mb-3">
                <h3 className="font-display text-base font-bold text-text">Parent-portal payments</h3>
                <p className="text-xs text-text-subtle">
                    Let parents pay their invoices online with a card. Stripe handles the
                    card form and the funds settle to your Stripe-connected account.
                </p>
            </div>

            {!stripeReady && (
                <p className="text-xs text-warning-text mb-3">
                    Finish connecting your Stripe account above before turning this on —
                    the portal Pay button only mounts when Stripe is live.
                </p>
            )}

            <form onSubmit={handleSubmit}>
                <label className="flex items-start gap-2 mb-4 text-sm text-text cursor-pointer">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => toggleEnabled(e.target.checked)}
                        disabled={disabled}
                        className="h-4 w-4 mt-0.5"
                        aria-label="Allow parents to pay invoices online"
                    />
                    <span>
                        Allow parents to pay invoices online
                        <span className="block text-text-subtle">
                            When off, the parent portal shows invoices but no Pay button — parents pay you off-line.
                        </span>
                    </span>
                </label>

                <FormField
                    label="Statement descriptor"
                    error={descriptorError ?? undefined}
                    hint={`Up to ${MAX_STATEMENT_DESCRIPTOR_LEN} characters. Shows on the parent's card statement. Avoid clinical words — anyone glancing at the statement should not be able to infer the client's condition.`}
                >
                    {(id) => (
                        <Input
                            id={id}
                            value={descriptor}
                            onChange={(e) => updateDescriptor(e.target.value)}
                            disabled={disabled}
                            placeholder={DEFAULT_STATEMENT_DESCRIPTOR}
                            maxLength={MAX_STATEMENT_DESCRIPTOR_LEN}
                            aria-invalid={!!descriptorError}
                        />
                    )}
                </FormField>

                {/*
                  Phase 8 of pma-stripe-payments. Autopay defaults are
                  per-practice — the per-invoice override lives on the
                  parent's invoice detail. Both keys are written even
                  when payments are disabled at the moment, so a
                  practice can stage their preferred defaults before
                  flipping payments on.
                */}
                <div className="mt-2 mb-4 pt-4 border-t border-border">
                    <div className="text-sm font-medium text-text mb-2">Autopay defaults</div>
                    <label className="flex items-start gap-2 mb-3 text-sm text-text cursor-pointer">
                        <input
                            type="checkbox"
                            checked={autopayDefault}
                            onChange={(e) => { setSaved(false); setAutopayDefault(e.target.checked); }}
                            disabled={disabled}
                            className="h-4 w-4 mt-0.5"
                        />
                        <span>
                            Default new invoices to autopay
                            <span className="block text-text-subtle">
                                When on, new invoices land with autopay already turned on. Parents can
                                still flip the per-invoice toggle off from the portal.
                            </span>
                        </span>
                    </label>
                    <FormField
                        label="Lead days before due date"
                        error={autopayLeadError ?? undefined}
                        hint="0 charges on the due date itself; a higher value charges N days before. Maximum 14."
                    >
                        {(id) => (
                            <Input
                                id={id}
                                type="number"
                                min={0}
                                max={14}
                                step={1}
                                value={autopayLeadDays}
                                onChange={(e) => {
                                    setSaved(false);
                                    setAutopayLeadDays(e.target.value);
                                    const check = validateAutopayLeadDays(e.target.value);
                                    setAutopayLeadError(check.error);
                                }}
                                disabled={disabled}
                                aria-invalid={!!autopayLeadError}
                            />
                        )}
                    </FormField>
                </div>

                {err && !descriptorError && !autopayLeadError && <p className="text-sm text-error mb-2">{err}</p>}
                {saved && <p className="text-sm text-success mb-2">Saved.</p>}

                <div className="flex justify-end">
                    <Button
                        variant="primary"
                        size="sm"
                        type="submit"
                        disabled={updateM.isPending || disabled || !!descriptorError || !!autopayLeadError}
                    >
                        {updateM.isPending ? 'Saving…' : 'Save'}
                    </Button>
                </div>
            </form>
        </Card>
    );
}

/*
 * Write-only logo control. The server stores bytes in `practice_logos` and
 * returns them via GET /api/settings/billing/logo. The endpoint is JWT-gated
 * and the browser doesn't attach the bearer token to a bare <img src>, so the
 * preview routes the request through the authenticated axios client and uses
 * a blob: URL; the upload timestamp doubles as the cache-bust key.
 */
function LogoSection() {
    const { data: status, isLoading } = useLogoStatus();
    const uploadM = useUploadLogo();
    const deleteM = useDeleteLogo();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [err, setErr] = useState<string | null>(null);
    const [justSaved, setJustSaved] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const hasLogo = Boolean(status?.hasValue);
    const previewSrc = useLogoBlobUrl(hasLogo, status?.uploadedAt);

    async function handleFile(file: File | null) {
        if (!file) return;
        setErr(null);
        setJustSaved(false);
        // Size preflight so we can surface the server's message without
        // burning bandwidth on an oversized upload.
        if (file.size > 500 * 1024) {
            setErr('Logo must be 500 KB or smaller.');
            return;
        }
        if (!['image/png', 'image/jpeg'].includes(file.type)) {
            setErr('Only PNG or JPEG images are accepted.');
            return;
        }
        try {
            await uploadM.mutateAsync(file);
            setJustSaved(true);
        } catch (e) {
            if (e instanceof AxiosError) {
                const data = e.response?.data as { error?: string } | undefined;
                setErr(data?.error ?? 'Could not upload logo.');
            } else {
                setErr('Could not upload logo.');
            }
        } finally {
            // Reset the file input so selecting the same file again triggers
            // a change event.
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    }

    async function performDelete() {
        setErr(null);
        setJustSaved(false);
        try {
            await deleteM.mutateAsync();
            setConfirmDeleteOpen(false);
        } catch (e) {
            setErr(extractErrorMessage(e, 'Could not remove logo.'));
        }
    }

    return (
        <div className="mb-5 pb-5 border-b border-border">
            <div className="mb-3">
                <h3 className="font-display text-base font-bold text-text">Invoice logo</h3>
                <p className="text-xs text-text-subtle">
                    PNG or JPEG, 500 KB max, up to 800×800. Printed in the header of every invoice PDF.
                </p>
            </div>

            <div className="mb-4 flex items-center gap-4">
                {isLoading ? (
                    <span className="text-text-subtle inline-flex items-center gap-1.5 text-sm">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                    </span>
                ) : hasLogo && previewSrc ? (
                    <>
                        <div className="h-20 w-20 border border-border rounded-md flex items-center justify-center bg-background overflow-hidden">
                            <img
                                src={previewSrc}
                                alt="Invoice logo preview"
                                className="max-h-full max-w-full object-contain"
                            />
                        </div>
                        <div className="text-sm text-text-muted">
                            <div className="text-text">{status?.originalName ?? 'logo'}</div>
                            {status?.uploadedAt && (
                                <div className="text-xs text-text-subtle">
                                    Uploaded {new Date(status.uploadedAt).toLocaleString(undefined, {
                                        year: 'numeric', month: 'short', day: '2-digit',
                                        hour: '2-digit', minute: '2-digit',
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="h-20 w-20 border border-dashed border-border rounded-md flex items-center justify-center text-text-subtle">
                        <ImageIcon className="w-6 h-6" />
                    </div>
                )}
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            />

            {err && <p className="text-sm text-error mb-2">{err}</p>}
            {justSaved && <p className="text-sm text-success mb-2">Uploaded.</p>}

            <div className="flex justify-between items-center">
                {hasLogo ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => { setErr(null); setConfirmDeleteOpen(true); }}
                        disabled={deleteM.isPending}
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                        {deleteM.isPending ? 'Removing…' : 'Remove'}
                    </Button>
                ) : <span />}
                <Button
                    variant="primary"
                    size="sm"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadM.isPending}
                >
                    <Upload className="w-3.5 h-3.5" />
                    {uploadM.isPending ? 'Uploading…' : hasLogo ? 'Replace logo' : 'Upload logo'}
                </Button>
            </div>

            <Dialog
                open={confirmDeleteOpen}
                onOpenChange={(o) => { if (!deleteM.isPending) setConfirmDeleteOpen(o); }}
            >
                <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>Remove invoice logo?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-text-muted">
                        Future invoices will render without a logo.
                    </p>
                    {err && <p className="mt-2 text-xs text-error">{err}</p>}
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost" size="sm" type="button">Cancel</Button>
                        </DialogClose>
                        <Button
                            variant="danger"
                            size="sm"
                            type="button"
                            onClick={performDelete}
                            disabled={deleteM.isPending}
                        >
                            {deleteM.isPending ? 'Removing…' : 'Remove'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
